import React, { useState, useEffect, useMemo } from 'react';
import * as PermissionDAO from '../../dao/PermissionDAO';
import './PermissionsTable.css';

const PERMISSION_TYPES = {
  READ: 'Read',
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
};

const COLUMN_TYPES = {
  READ: 'Read',
  CREATE_UPDATE: 'Create/Update',
  DELETE: 'Delete',
};

const CATEGORY_ORDER = ['Brand Intelligence', 'Content Studio', 'Settings'];
const UNCATEGORIZED_KEY = '__UNCATEGORIZED__';

const PermissionsTable = ({ value, onChange }) => {
  const [permissionsByFeature, setPermissionsByFeature] = useState({});
  const [categorizedFeatures, setCategorizedFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  
  const selectedIds = useMemo(() => new Set(value || []), [value]);

  useEffect(() => {
    const fetchAndProcessPermissions = async () => {
      try {
        setLoading(true);
        const response = await PermissionDAO.getAll();
        const allPermissions = response.data.list;

        const groupedData = allPermissions.reduce((acc, permission) => {
          if (!permission.feature || permission.isActive === false) return acc;
          
          acc.byFeature[permission.feature] = acc.byFeature[permission.feature] || {};
          acc.byFeature[permission.feature][permission.type] = permission;
          
          const category = permission.category || UNCATEGORIZED_KEY;
          acc.byCategory[category] = acc.byCategory[category] || {};
          
          if (!acc.byCategory[category][permission.feature]) {
            acc.byCategory[category][permission.feature] = permission.order || 999;
          }

          return acc;
        }, { byFeature: {}, byCategory: {} });
        
        const finalCategorizedFeatures = {};
        for (const category in groupedData.byCategory) {
          const featuresWithOrder = Object.entries(groupedData.byCategory[category]);
          featuresWithOrder.sort((a, b) => a[1] - b[1]);
          finalCategorizedFeatures[category] = featuresWithOrder.map(item => item[0]);
        }
        
        setPermissionsByFeature(groupedData.byFeature);
        setCategorizedFeatures(finalCategorizedFeatures);
      } catch (error) {
        console.error("Failed to fetch or process permissions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessPermissions();
  }, []);
  
  const allVisibleFeatures = useMemo(() => Object.values(categorizedFeatures).flat(), [categorizedFeatures]);

  const availableIdsByColumn = useMemo(() => {
    const result = {
      [COLUMN_TYPES.READ]: [],
      [COLUMN_TYPES.CREATE_UPDATE]: [],
      [COLUMN_TYPES.DELETE]: [],
    };

    allVisibleFeatures.forEach(feature => {
      const featurePerms = permissionsByFeature[feature];
      if (!featurePerms) return;
      
      if (featurePerms[PERMISSION_TYPES.READ]) result[COLUMN_TYPES.READ].push(featurePerms[PERMISSION_TYPES.READ].id);
      if (featurePerms[PERMISSION_TYPES.CREATE]) result[COLUMN_TYPES.CREATE_UPDATE].push(featurePerms[PERMISSION_TYPES.CREATE].id);
      if (featurePerms[PERMISSION_TYPES.UPDATE]) result[COLUMN_TYPES.CREATE_UPDATE].push(featurePerms[PERMISSION_TYPES.UPDATE].id);
      if (featurePerms[PERMISSION_TYPES.DELETE]) result[COLUMN_TYPES.DELETE].push(featurePerms[PERMISSION_TYPES.DELETE].id);
    });
    return result;
  }, [allVisibleFeatures, permissionsByFeature]);

  const handleSelectAll = (columnType, isChecked) => {
    const newSelectedIds = new Set(selectedIds);
    const idsForColumn = availableIdsByColumn[columnType];
    
    idsForColumn.forEach(id => {
      if (isChecked) {
        newSelectedIds.add(id);
      } else {
        newSelectedIds.delete(id);
      }
    });

    if (onChange) {
      onChange(Array.from(newSelectedIds));
    }
  };
  
  const isAllSelected = (columnType) => {
    const idsForColumn = availableIdsByColumn[columnType];
    if (idsForColumn.length === 0) return false;
    return idsForColumn.every(id => selectedIds.has(id));
  };
  
  const handleCheckboxChange = (feature, columnType, isChecked) => {
    const featurePermissions = permissionsByFeature[feature];
    if (!featurePermissions) return;
    
    let idsToUpdate = [];

    if (columnType === COLUMN_TYPES.READ) {
      if (featurePermissions[PERMISSION_TYPES.READ]) idsToUpdate.push(featurePermissions[PERMISSION_TYPES.READ].id);
    } else if (columnType === COLUMN_TYPES.CREATE_UPDATE) {
      if (featurePermissions[PERMISSION_TYPES.CREATE]) idsToUpdate.push(featurePermissions[PERMISSION_TYPES.CREATE].id);
      if (featurePermissions[PERMISSION_TYPES.UPDATE]) idsToUpdate.push(featurePermissions[PERMISSION_TYPES.UPDATE].id);
    } else if (columnType === COLUMN_TYPES.DELETE) {
      if (featurePermissions[PERMISSION_TYPES.DELETE]) idsToUpdate.push(featurePermissions[PERMISSION_TYPES.DELETE].id);
    }
    
    const newSelectedIds = new Set(selectedIds);
    idsToUpdate.forEach(id => {
      if (isChecked) newSelectedIds.add(id);
      else newSelectedIds.delete(id);
    });
    
    if (onChange) onChange(Array.from(newSelectedIds));
  };

  const isChecked = (feature, columnType) => {
    const featurePermissions = permissionsByFeature[feature];
    if (!featurePermissions) return false;
    if (columnType === COLUMN_TYPES.READ) {
      const readPerm = featurePermissions[PERMISSION_TYPES.READ];
      return readPerm ? selectedIds.has(readPerm.id) : false;
    }
    if (columnType === COLUMN_TYPES.CREATE_UPDATE) {
      const createPerm = featurePermissions[PERMISSION_TYPES.CREATE];
      const updatePerm = featurePermissions[PERMISSION_TYPES.UPDATE];
      return (createPerm && selectedIds.has(createPerm.id)) || (updatePerm && selectedIds.has(updatePerm.id));
    }
    if (columnType === COLUMN_TYPES.DELETE) {
      const deletePerm = featurePermissions[PERMISSION_TYPES.DELETE];
      return deletePerm ? selectedIds.has(deletePerm.id) : false;
    }
    return false;
  };
  
  const isDisabled = (feature, columnType) => {
    const featurePermissions = permissionsByFeature[feature];
    if (!featurePermissions) return true;
    if (columnType === COLUMN_TYPES.READ) return !featurePermissions[PERMISSION_TYPES.READ];
    if (columnType === COLUMN_TYPES.CREATE_UPDATE) return !featurePermissions[PERMISSION_TYPES.CREATE] && !featurePermissions[PERMISSION_TYPES.UPDATE];
    if (columnType === COLUMN_TYPES.DELETE) return !featurePermissions[PERMISSION_TYPES.DELETE];
    return true;
  };

  const FEATURE_DISPLAY_NAMES = {
    SourceContent: 'Model',
    Assets: 'Trust Signals',
  };

  const formatFeatureName = (name) => {
    if (!name) return '';
    if (FEATURE_DISPLAY_NAMES[name]) return FEATURE_DISPLAY_NAMES[name];
    return name.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const renderFeatureRow = (feature) => (
    <tr key={feature} className="feature-row">
      <td className="feature-name">{formatFeatureName(feature)}</td>
      <td>
        {!isDisabled(feature, COLUMN_TYPES.READ) && (
          <input type="checkbox" checked={isChecked(feature, COLUMN_TYPES.READ)}
            onChange={(e) => handleCheckboxChange(feature, COLUMN_TYPES.READ, e.target.checked)} />
        )}
      </td>
      <td>
        {!isDisabled(feature, COLUMN_TYPES.CREATE_UPDATE) && (
          <input type="checkbox" checked={isChecked(feature, COLUMN_TYPES.CREATE_UPDATE)}
            onChange={(e) => handleCheckboxChange(feature, COLUMN_TYPES.CREATE_UPDATE, e.target.checked)} />
        )}
      </td>
      <td>
        {!isDisabled(feature, COLUMN_TYPES.DELETE) && (
          <input type="checkbox" checked={isChecked(feature, COLUMN_TYPES.DELETE)}
            onChange={(e) => handleCheckboxChange(feature, COLUMN_TYPES.DELETE, e.target.checked)} />
        )}
      </td>
    </tr>
  );

  const sortedCategories = Object.keys(categorizedFeatures).sort((a, b) => {
    if (a === UNCATEGORIZED_KEY) return -1;
    if (b === UNCATEGORIZED_KEY) return 1;
    const indexA = CATEGORY_ORDER.indexOf(a);
    const indexB = CATEGORY_ORDER.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  if (loading) return <div>Loading Permissions...</div>;

  return (
    <div className="permissions-table-container">
      <table className="permissions-table">
        <thead>
          <tr>
            <th>Feature</th>
            <th>
              <span>View</span>
              {availableIdsByColumn[COLUMN_TYPES.READ].length > 0 && (
                <input type="checkbox"
                  checked={isAllSelected(COLUMN_TYPES.READ)}
                  onChange={(e) => handleSelectAll(COLUMN_TYPES.READ, e.target.checked)}
                />
              )}
            </th>
            <th>
              <span>Add & Edit</span>
              {availableIdsByColumn[COLUMN_TYPES.CREATE_UPDATE].length > 0 && (
                <input type="checkbox"
                  checked={isAllSelected(COLUMN_TYPES.CREATE_UPDATE)}
                  onChange={(e) => handleSelectAll(COLUMN_TYPES.CREATE_UPDATE, e.target.checked)}
                />
              )}
            </th>
            <th>
              <span>Delete</span>
              {availableIdsByColumn[COLUMN_TYPES.DELETE].length > 0 && (
                <input type="checkbox"
                  checked={isAllSelected(COLUMN_TYPES.DELETE)}
                  onChange={(e) => handleSelectAll(COLUMN_TYPES.DELETE, e.target.checked)}
                />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedCategories.map(category => (
            <React.Fragment key={category}>
              {category !== UNCATEGORIZED_KEY && (
                <tr className="category-row">
                  <td colSpan="4">{category}</td>
                </tr>
              )}
              {categorizedFeatures[category].map(renderFeatureRow)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionsTable;